import { StudentLayout } from 'src/components/Layout/student/StudentLayout';
import { AuthorizedUserProps } from 'src/routes/components/PrivateRoute';
import { Helmet } from 'react-helmet-async';
import React, { FC, ReactElement, useEffect, useMemo, useState } from 'react';
import './module.scss';
import './table.scss';
import {
    Checkbox,
    Col,
    ConfigProvider,
    DatePicker,
    Dropdown,
    Empty,
    Row,
    Select,
    Table,
    TablePaginationConfig,
} from 'antd';
import { ArrowDownIcon } from 'src/icons/ArrowDown';
import locale from 'antd/lib/date-picker/locale/ru_RU';
import dayjs, { Dayjs } from 'dayjs';
import { DatePickerProps } from 'antd/es/date-picker';
import { Group, ResponseError, useGetGroupsQuery } from 'src/api';
import { map } from 'lodash';
import classNames from 'classnames';
import {
    useChangeAttendanceReasonMutation,
    useDropAttendanceReasonMutation,
    useSearchAttendanceInHistoryQuery,
} from 'src/api/endpoints/attendance';
import { AvatarWithDisplayValueColumn } from 'src/components/DataTable';
import { getUserFullname } from 'src/utils/getFullname';
import {
    AttendanceHistory,
    AttendanceStatuses,
} from 'src/@types/modules/attendance/attendance.types';
import {
    DeleteOutlined,
    EditOutlined,
    EyeOutlined,
    MoreOutlined,
    UserOutlined,
} from '@ant-design/icons';
import { ApplicationModal } from 'src/components/Modal/LegacyModal';
import { FormikErrors, useFormik } from 'formik';
import { Form } from 'src/components/design-system/form';
import { FormButtons } from 'src/components/design-system/form/buttons';
import { TextField } from 'src/forms/DebitStudentCoins';
import { notifyAboutException, notifyAboutSuccess } from 'src/utils/notify';
import { MainSection } from 'src/components/design-system/struct/MainSection';

type EditReasonForm = {
    reason: string;
    isReasonValid: boolean;
};

export type DateRangePickerProps = {
    start?: Dayjs | null;
    end?: Dayjs | null;
    defaultValues?: { start: Dayjs | null; end: Dayjs | null };
    onChange?: (values: { start: Dayjs; end: Dayjs }) => void;
};

// TODO: Поле даты

export const DateRangePicker: FC<DateRangePickerProps> = (
    props: DateRangePickerProps
) => {
    const { defaultValues = { start: null, end: null }, start, end } = props;

    const [startValue, setStartValue] = useState<Dayjs | null>(
        start ?? defaultValues.start
    );

    const [endValue, setEndValue] = useState<Dayjs | null>(
        end ?? defaultValues.end
    );

    useEffect(() => {
        if (start === undefined) return;
        setStartValue(start);
    }, [start]);

    useEffect(() => {
        if (end === undefined) return;
        setEndValue(end);
    }, [end]);

    useEffect(() => {
        if (!startValue || !endValue) return;

        if (!props.onChange) return;

        props.onChange({ start: startValue, end: endValue });
    }, [startValue, endValue, props.onChange]);

    const datePickerProps: Partial<DatePickerProps> = {
        className: 'date-picker',
        locale: {
            ...locale,
            lang: {
                ...locale.lang,
                fieldDateFormat: 'DD.MM.YYYY',
                monthFormat: 'MMMM',
                monthBeforeYear: false,
            },
        },
        showNow: false,
        cellRender: (date, info) => {
            if (info.type === 'month') {
                return dayjs(date).format('MMM');
            }

            return info.originNode;
        },
    };

    return (
        <div className={'date-range-picker'}>
            <DatePicker
                {...datePickerProps}
                value={startValue}
                defaultValue={defaultValues.start}
                onChange={setStartValue}
            />
            —
            <DatePicker
                {...datePickerProps}
                value={endValue}
                defaultValue={defaultValues.end}
                onChange={setEndValue}
            />
        </div>
    );
};

export const AttendanceHistoryPage: FC<AuthorizedUserProps> = (
    props: AuthorizedUserProps
) => {
    const defaultRangeValue = useMemo(
        () => ({ start: dayjs().startOf('week'), end: dayjs() }),
        []
    );

    const [rangeMode, setRangeMode] = useState<'all' | 'period'>('period');
    const [selectedGroupId, selectGroupId] = useState<number | null>(null);

    const [selectedStatuses, selectStatuses] = useState<number[]>([1]);

    const [isValidReason, setIsValidReason] = useState<boolean | null>(false);
    const [range, setRange] = useState<{ start: Dayjs; end: Dayjs }>(
        defaultRangeValue
    );

    const [changeReason, { isLoading: isReasonChanging }] =
        useChangeAttendanceReasonMutation();

    const [config, setConfig] = useState({
        page: 1,
        size: 100,
    });

    const { data } = useSearchAttendanceInHistoryQuery({
        search: {
            hasValidReason: isValidReason ?? undefined,
            statuses: selectedStatuses,
            groupId: selectedGroupId ?? undefined,
            ...(rangeMode === 'period'
                ? { from: range.start.unix(), to: range.end.unix() }
                : {}),
            size: config.size,
            page: config.page - 1,
        },
    });

    const { data: groups, isLoading: isGroupsLoading } = useGetGroupsQuery({
        params: { branchId: props.currentBranchId },
    });

    const [reasonEditModalTarget, setReasonEditModalTarget] =
        useState<AttendanceHistory | null>(null);

    const [isReasonEditModalOpen, setReasonEditModalOpen] =
        useState<boolean>(false);

    const closeEditReasonModal = () => {
        setReasonEditModalOpen(false);
        setReasonEditModalTarget(null);
    };

    const [deleteReason] = useDropAttendanceReasonMutation();

    const onReasonDelete = (attendance: AttendanceHistory) => {
        deleteReason({
            params: {
                branchId: props.currentBranchId,
                attendanceId: attendance.id,
            },
        })
            .unwrap()
            .then(() => {
                notifyAboutSuccess({ content: 'Причина успешно сброшена' });
            })
            .catch((reason: ResponseError) => {
                notifyAboutException({ content: reason.message });
            });
    };

    const onReasonChange = (
        target: AttendanceHistory,
        values: Required<EditReasonForm>
    ): Promise<void> => {
        return changeReason({
            payload: {
                reason: values.reason,
                isValid: values.isReasonValid,
            },
            params: {
                attendanceId: target.id,
                branchId: props.currentBranchId,
            },
        })
            .unwrap()
            .then(() => {
                notifyAboutSuccess({ content: 'Причина успешно изменена' });
                closeEditReasonModal();
            })
            .catch((reason: ResponseError) => {
                notifyAboutException({ content: reason.message });
            });
    };

    return (
        <StudentLayout auth={props}>
            {reasonEditModalTarget && (
                <ApplicationModal
                    header={'Изменение причины'}
                    isOpen={isReasonEditModalOpen}
                    onClose={closeEditReasonModal}
                >
                    <AttendanceHistoryReasonForm
                        isLoading={isReasonChanging}
                        onSubmit={onReasonChange}
                        onCancel={closeEditReasonModal}
                        target={reasonEditModalTarget}
                    />
                </ApplicationModal>
            )}
            <Helmet
                title={`История посещений · ${props.currentBranch?.name} · Личный кабинет`}
            />
            <MainSection title={'История посещений'}>
                <div className="filters">
                    <Row style={{ width: '100%', marginTop: '-6px' }}>
                        <Col
                            xs={24}
                            md={8}
                            lg={6}
                            xl={6}
                            style={{ padding: '8px 6px' }}
                        >
                            <Select
                                suffixIcon={
                                    <ArrowDownIcon
                                        className={'select-input-icon'}
                                        color={'#092433'}
                                    />
                                }
                                classNames={{
                                    root: 'select-input range-mode-input',
                                }}
                                options={[
                                    {
                                        label: 'Все время',
                                        value: 'all',
                                    },
                                    {
                                        label: 'Выбранный период',
                                        value: 'period',
                                    },
                                ]}
                                onSelect={(v: string) => {
                                    setRangeMode(v as 'all' | 'period');
                                }}
                                value={rangeMode}
                                placeholder={'Выберите статус регистрации'}
                            />
                        </Col>
                        {rangeMode === 'period' && (
                            <Col
                                xs={24}
                                md={16}
                                lg={12}
                                xl={10}
                                style={{ padding: '8px 6px' }}
                            >
                                <DateRangePicker
                                    defaultValues={defaultRangeValue}
                                    onChange={setRange}
                                />
                            </Col>
                        )}
                    </Row>
                    <Row style={{ width: '100%' }}>
                        <Col
                            xs={24}
                            sm={24}
                            md={24}
                            lg={6}
                            xl={6}
                            style={{ padding: '8px 6px' }}
                        >
                            <Select<'all' | number>
                                suffixIcon={
                                    <ArrowDownIcon
                                        className={'select-input-icon'}
                                        color={'#092433'}
                                    />
                                }
                                classNames={{
                                    root: 'select-input range-mode-input',
                                }}
                                options={[
                                    {
                                        label: 'Все группы',
                                        value: 'all',
                                    },
                                    ...map(groups, (g: Group) => ({
                                        label: g.name,
                                        value: g.id,
                                    })),
                                ]}
                                value={selectedGroupId ?? 'all'}
                                loading={isGroupsLoading}
                                onSelect={(v: 'all' | number) => {
                                    if (v === 'all') {
                                        selectGroupId(null);
                                        return;
                                    }

                                    selectGroupId(v);
                                }}
                                placeholder={'Выберите группу'}
                                optionFilterProp={'label'}
                                showSearch={true}
                            />
                        </Col>
                        <Col
                            xs={24}
                            sm={12}
                            md={12}
                            lg={6}
                            xl={6}
                            style={{ padding: '8px 6px' }}
                        >
                            <Select<number[]>
                                showSearch={false}
                                maxTagCount={1}
                                maxTagPlaceholder={values => {
                                    return `+${values.length}`;
                                }}
                                tagRender={value => {
                                    return (
                                        <div
                                            className={classNames(
                                                'multiple-select-item',
                                                {
                                                    max: value.isMaxTag,
                                                }
                                            )}
                                        >
                                            {value.label}
                                        </div>
                                    );
                                }}
                                suffixIcon={
                                    <ArrowDownIcon
                                        className={'select-input-icon'}
                                        color={'#092433'}
                                    />
                                }
                                mode={'multiple'}
                                classNames={{
                                    root: 'select-input multiple placeholder-as-value',
                                }}
                                options={[
                                    {
                                        label: 'Присутствующие',
                                        value: 0,
                                    },
                                    {
                                        label: 'Отсутствующие',
                                        value: 1,
                                    },
                                    {
                                        label: 'Опоздавшие',
                                        value: 2,
                                    },
                                    {
                                        label: 'Не отмеченные',
                                        value: 3,
                                    },
                                ]}
                                onChange={(v: number[]) => {
                                    selectStatuses(v);
                                }}
                                value={selectedStatuses}
                                placeholder={'Все учебные статусы'}
                            />
                        </Col>

                        <Col
                            xs={24}
                            sm={12}
                            md={12}
                            lg={12}
                            xl={12}
                            style={{ padding: '8px 6px' }}
                        >
                            <Select<string | null>
                                suffixIcon={
                                    <ArrowDownIcon
                                        className={'select-input-icon'}
                                        color={'#092433'}
                                    />
                                }
                                classNames={{
                                    root: 'select-input',
                                }}
                                options={[
                                    {
                                        label: 'Любая степень уважительности',
                                        value: null,
                                    },
                                    {
                                        label: 'По уважительной причине',
                                        value: 'valid-only',
                                    },
                                    {
                                        label: 'По неуважительной причине',
                                        value: 'invalid-only',
                                    },
                                ]}
                                onChange={(v: string | null) => {
                                    if (v === null)
                                        return setIsValidReason(null);

                                    setIsValidReason(v === 'valid-only');
                                }}
                                value={
                                    isValidReason === null
                                        ? null
                                        : isValidReason
                                          ? 'valid-only'
                                          : 'invalid-only'
                                }
                                placeholder={'Статус уважительной причины'}
                            />
                        </Col>
                    </Row>
                </div>

                <div
                    className={'table'}
                    style={{ marginTop: '24px', width: '100%' }}
                >
                    <ConfigProvider
                        renderEmpty={() => {
                            return (
                                <Empty
                                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                                    description={
                                        'Отметки посещаемости не найдены'
                                    }
                                />
                            );
                        }}
                    >
                        <Table<AttendanceHistory>
                            tableLayout={'fixed'}
                            dataSource={data?.content}
                            onChange={(pagination: TablePaginationConfig) => {
                                setConfig({
                                    size: 100,
                                    page: pagination.current ?? 0,
                                });

                                if (pagination.current !== config.page)
                                    window.scrollTo({ top: 0 });
                            }}
                            rowKey={s => s.id}
                            pagination={{
                                hideOnSinglePage: true,
                                responsive: true,
                                showSizeChanger: false,
                                showQuickJumper: false,
                                total: data?.page?.totalElements,
                                pageSize: config.size,
                                current: config.page,
                                position: ['bottomRight'],
                            }}
                        >
                            <Table.Column<AttendanceHistory>
                                width={'35%'}
                                title={'Студент'}
                                render={(_, { student }) => (
                                    <AvatarWithDisplayValueColumn
                                        data={{
                                            userId: student.id,
                                            displayValue:
                                                getUserFullname(student),
                                            description: student.contractNumber,
                                        }}
                                        link={`/b/${props.currentBranchId}/students/${student.studentId}`}
                                    />
                                )}
                            />

                            <Table.Column<AttendanceHistory>
                                width={'25%'}
                                title={'Занятие'}
                                render={(_, history: AttendanceHistory) => {
                                    const lessonDate: string = dayjs(
                                        history.lesson.startedAt
                                    ).format('DD.MM.YYYY HH:mm');

                                    return (
                                        <>
                                            <div
                                                className={'lesson-date'}
                                                style={{
                                                    fontSize: '12px',
                                                    color: '#5f717a',
                                                }}
                                            >
                                                {lessonDate}
                                            </div>
                                            <div
                                                className={'lesson'}
                                                style={{
                                                    fontSize: '14px',
                                                    color: '#092433',
                                                    textWrap: 'nowrap',
                                                    textOverflow: 'ellipsis',
                                                    overflow: 'hidden',
                                                }}
                                            >
                                                {history.lesson.subject.name}
                                            </div>
                                        </>
                                    );
                                }}
                            />

                            <Table.Column<AttendanceHistory>
                                width={'15%'}
                                title={'Статус'}
                                render={(_, history: AttendanceHistory) => {
                                    const statusesLabel: Record<
                                        AttendanceStatuses,
                                        string
                                    > = {
                                        [AttendanceStatuses.NotMarked]:
                                            'Не отмечен',
                                        [AttendanceStatuses.Present]:
                                            'Присутствовал',
                                        [AttendanceStatuses.Late]: 'Опоздал',
                                        [AttendanceStatuses.Absent]:
                                            'Отсутствовал',
                                    };

                                    return (
                                        <>{statusesLabel[history.status.id]}</>
                                    );
                                }}
                            />

                            <Table.Column<AttendanceHistory>
                                width={'25%'}
                                title={'Причина'}
                                render={(_, history: AttendanceHistory) => {
                                    const { hasValidReason, reason } = history;

                                    return (
                                        <>
                                            {hasValidReason !== null && (
                                                <ReasonValidLabel
                                                    valid={hasValidReason}
                                                />
                                            )}
                                            <div
                                                className="reason"
                                                style={{
                                                    color: reason
                                                        ? '#092433'
                                                        : '#5f717a',
                                                }}
                                            >
                                                {reason ?? 'Не указана'}
                                            </div>
                                        </>
                                    );
                                }}
                            />
                            <Table.Column<AttendanceHistory>
                                width={'50px'}
                                render={(record: AttendanceHistory) => (
                                    <Dropdown
                                        overlayClassName={'actions-dropdown'}
                                        arrow={false}
                                        placement={'bottomRight'}
                                        trigger={['click']}
                                        menu={{
                                            onClick: ({ key }) => {
                                                if (key === 'lesson') {
                                                    window.location.href = `/b/${props.currentBranchId}/attendance?groupId=${record.lesson.group.id}&subjectId=${record.lesson.subject.id}&lessonId=${record.lesson.id}`;
                                                } else if (key === 'student') {
                                                    window.location.href = `/b/${props.currentBranchId}/students/${record.student.id}`;
                                                } else if (
                                                    key === 'edit-reason'
                                                ) {
                                                    setReasonEditModalTarget(
                                                        record
                                                    );
                                                    setReasonEditModalOpen(
                                                        true
                                                    );
                                                } else if (
                                                    key === 'delete-reason'
                                                ) {
                                                    onReasonDelete(record);
                                                }
                                            },
                                            items: [
                                                {
                                                    key: '1',
                                                    label: 'Действия',
                                                    disabled: true,
                                                },
                                                {
                                                    type: 'divider',
                                                },
                                                {
                                                    key: 'lesson',
                                                    label: 'Перейти к занятию',
                                                    icon: <EyeOutlined />,
                                                },
                                                {
                                                    key: 'student',
                                                    label: 'Перейти к студенту',
                                                    icon: <UserOutlined />,
                                                },
                                                {
                                                    type: 'divider',
                                                },
                                                ...([
                                                    AttendanceStatuses.Late,
                                                    AttendanceStatuses.Absent,
                                                ].includes(record.status.id)
                                                    ? [
                                                          {
                                                              key: 'edit-reason',
                                                              label: 'Изменить причину',
                                                              icon: (
                                                                  <EditOutlined />
                                                              ),
                                                          },
                                                      ]
                                                    : []),
                                                ...(record.reason !== null
                                                    ? [
                                                          {
                                                              key: 'delete-reason',
                                                              label: 'Удалить причину',
                                                              icon: (
                                                                  <DeleteOutlined />
                                                              ),
                                                          },
                                                      ]
                                                    : []),
                                            ],
                                        }}
                                    >
                                        <MoreOutlined />
                                    </Dropdown>
                                )}
                            />
                        </Table>
                    </ConfigProvider>
                </div>
            </MainSection>
        </StudentLayout>
    );
};

export type AttendanceHistoryReasonFormProps = {
    isLoading?: boolean;
    target: AttendanceHistory;
    onSubmit: (
        target: AttendanceHistory,
        values: Required<EditReasonForm>
    ) => Promise<void>;
    onCancel: () => void;
};

export const AttendanceHistoryReasonForm: FC<
    AttendanceHistoryReasonFormProps
> = props => {
    const { target, onSubmit, onCancel, isLoading } = props;

    const formik = useFormik<Partial<EditReasonForm>>({
        initialValues: {
            reason: target.reason ?? undefined,
            isReasonValid: target.hasValidReason ?? false,
        },
        validate: (values: Partial<EditReasonForm>) => {
            const { reason } = values;

            const errors: FormikErrors<EditReasonForm> = {};

            if (!reason || reason.length < 5) {
                errors.reason = 'Поле обязательно к заполнению';
            }

            return errors;
        },
        onSubmit: (values: Partial<EditReasonForm>) => {
            const { reason, isReasonValid } = values;

            if (!reason || isReasonValid === undefined) return;

            return onSubmit(target, {
                reason,
                isReasonValid,
            });
        },
    });

    return (
        <Form formik={formik} prohibitAutoComplete={true}>
            <TextField
                name={'reason'}
                formik={formik}
                label={'Причина'}
                placeholder={'Введите причину'}
                checkTouched={true}
            />
            <Checkbox
                onChange={e => {
                    formik.setFieldValue('isReasonValid', e.target.checked);
                }}
                checked={formik.values.isReasonValid}
            >
                Уважительная причина
            </Checkbox>
            <FormButtons
                isSubmitLoading={isLoading}
                formik={formik}
                submitLabel={'Применить'}
                onCancel={onCancel}
            />
        </Form>
    );
};

export type ReasonValidLabelProps = {
    valid: boolean;
};

export const ReasonValidLabel: FC<ReasonValidLabelProps> = (
    props: ReasonValidLabelProps
): ReactElement => {
    const { valid } = props;

    return (
        <div
            className={classNames('reason-valid-label', { invalid: !valid })}
            style={{
                fontSize: '12px',
            }}
        >
            {valid ? 'Уважительная' : 'Неуважительная'}
        </div>
    );
};
